import { useEffect, useState, useCallback } from 'react';
import { storageKeys } from 'settings';
import APIHandler from 'config/APIHandler';
import DataHandler from 'config/DataHandler';
import StorageHandler from 'config/StorageHandler';

/**
 * An updated Pointshandler that uses hooks instead of classes.
 * WARNING: Work in progress. Not all functionality included in the class-based PointsHandler is implemented yet.
 *
 * @returns {Object} An object containing the following properties:
 *
 * @param {boolean} isLoading: A boolean indicating whether the data is currently being fetched.
 * @param {object} tiers: An array of tier objects, each containing a title and a points value.
 * @param {number} totalPoints: The total number of points the user has earned.
 * @param {number} currentTierIndex: The index of the current tier.
 * @param {number} completedItems: The total items the user has completed.
 * @param {function} refreshPointsData: A function that can be called to refresh the points data manually if so desired.
 * @param {number} diplomaProgress: The progress of the diploma in percentage.
 */
const usePointsHandler = () => {
  const [isLoading, setIsLoading] = useState(false);

  const [appId, setAppId] = useState(null);
  const [lang, setLang] = useState(null);
  const [user, setUser] = useState(null);

  const [tiers, setTiers] = useState(null);
  const [totalPoints, setTotalPoints] = useState(null);
  const [completedItems, setCompletedItems] = useState(null);
  const [currentTierIndex, setCurrentTierIndex] = useState(null);

  const [diplomaProgress, setDiplomaProgress] = useState(0);

  const errorMessages = {
    noUserData: 'Failed to get user information. User may not be logged in or improper props were provided.',
    noPointsData: 'Failed to get points data.',
  };

  // sets userData from either props or localStorage and returns true if successful
  const getUserData = useCallback(async () => {
    setIsLoading(true);

    const id = await DataHandler.matchAppId();
    const user = await StorageHandler.load(storageKeys.user);
    const lang = await StorageHandler.load(storageKeys.lang);

    if (id) setAppId(id);
    if (user) setUser(user);
    if (lang) setLang(lang);
    setIsLoading(false);
  }, []);

  // sets / refreshes points data from API
  const getPointsData = useCallback(async () => {
    if (!appId || !user?.email) return;

    setIsLoading(true);

    const { email } = user;
    const { result, response } = await APIHandler.getUserPointsNew(appId, email);

    if (result === 'success') {
      const { tiers: tiersData, totalPoints: totalPointsData, completedItems: completedItemsData } = response;

      setTiers(tiersData);
      setTotalPoints(totalPointsData);
      setCompletedItems(completedItemsData);
      setIsLoading(false);
    } else {
      throw new Error(errorMessages.noPointsData);
    }

    setIsLoading(false);
  }, [appId, user]);

  // calculates the current tierindex based on the total points. Returns null if no tiers or totalPoints are available, or if no tier is reached yet.
  const updateCurrentTierIndex = useCallback(() => {
    if (!tiers || !totalPoints) return null;

    setIsLoading(true);

    const highestTier = tiers.reduce((acc, tier, i) => {
      if (totalPoints >= tier.points) {
        return i;
      }
      return acc;
    }, null);

    setCurrentTierIndex(highestTier);

    setIsLoading(false);
  }, [tiers, totalPoints]);

  // calculates the diploma progress based on the first 10 chapters. Returns null if no chapters are available.
  const getDiplomaProgress = useCallback(async () => {
    if (!(appId && user && lang)) return null;
    setIsLoading(true);

    const { training_graduated, email } = user;

    // if (training_graduated && training_graduated !== '0000-00-00 00:00:00' && training_graduated !== 'null') {
    //   setDiplomaProgress(100);
    //   return;
    // }

    const { status, data } = await DataHandler.checkKey(storageKeys.progress, APIHandler.getUserProgress(appId, email));

    if (status === 'error') return;

    const { chapters: progress } = data[0];

    // remove non-chapter (canadian)

    // block if no chapters are available. Treated as 0 by default
    if (progress.length === 0) {
      setDiplomaProgress(0);
      return;
    }

    let completedChaptersCount = 0;
    progress.forEach((chapter, i) => {
      const { total_pages, progress: finished_pages } = chapter;
      if (finished_pages >= total_pages - 1) {
        completedChaptersCount += 1;
      }
    });

    const percentage = Math.round((completedChaptersCount / progress.length) * 100);
    setDiplomaProgress(percentage >= 97 ? 100 : percentage);

    setIsLoading(false);
  }, [appId, user, lang]);

  // Primary 'useEffect' hook that fetches user data and updates state
  useEffect(() => {
    const fetchData = async () => {
      await getUserData();
    };
    fetchData();
  }, [getUserData]);

  // Secondary useEffectHook that calculates the diploma progress and updates state. Triggers by changes in 'appId', 'user' and 'lang'
  useEffect(() => {
    const fetchData = async () => {
      await getDiplomaProgress();
    };
    fetchData();
  }, [getDiplomaProgress]);

  // Secondary 'useEffect' hook that fetches the points data and updates state. Triggers by changes in 'appId' and 'email'
  useEffect(() => {
    const fetchData = async () => {
      await getPointsData();
    };
    fetchData();
  }, [getPointsData]);

  // Tertiary 'useEffect' hook that calculates the current tier and updates state. Triggers by changes in 'tiers' and 'totalPoints'
  useEffect(() => {
    updateCurrentTierIndex();
  }, [tiers, totalPoints]);

  return {
    isLoading,
    tiers,
    totalPoints,
    currentTierIndex,
    completedItems,
    diplomaProgress,
    refreshPointsData: getPointsData,
  };
};

export default usePointsHandler;
